import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { Download, Copy, Eye, EyeOff, Settings } from 'lucide-react';
import { backendUrl } from '../../utils/socketClient';

interface QRGeneratorProps {
  shopName: string;
  userEmail: string;
}

interface QRSettings {
  size: number;
  backgroundColor: string;
  foregroundColor: string;
  includeShopName: boolean;
  customMessage: string;
}

const QRGenerator: React.FC<QRGeneratorProps> = ({ shopName, userEmail }) => {
  const [settings, setSettings] = useState<QRSettings>({
    size: 200,
    backgroundColor: '#FFFFFF',
    foregroundColor: '#000000',
    includeShopName: true,
    customMessage: 'お食事の感想をお聞かせください！'
  });

  const [showSettings, setShowSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 設定を読み込み
  useEffect(() => {
    loadQRSettings();
  }, [shopName, userEmail]);

  const loadQRSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/qr-settings/${encodeURIComponent(shopName)}?user_email=${encodeURIComponent(userEmail)}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // デフォルト値でマージして設定
          setSettings({
            size: result.settings.size ?? 200,
            backgroundColor: result.settings.background_color ?? '#FFFFFF',
            foregroundColor: result.settings.foreground_color ?? '#000000',
            includeShopName: result.settings.include_shop_name ?? true,
            customMessage: result.settings.custom_message ?? 'お食事の感想をお聞かせください！'
          });
        }
      } else {
        console.error('QR設定読み込みエラー:', response.status);
      }
    } catch (error) {
      console.error('QR設定読み込み中にエラーが発生しました:', error);
    } finally {
      setLoading(false);
    }
  };

  // 設定を保存
  const saveQRSettings = async () => {
    setSaving(true);
    try {
      // APIに送信するデータ形式に変換
      const saveData = {
        size: settings.size,
        background_color: settings.backgroundColor,
        foreground_color: settings.foregroundColor,
        include_shop_name: settings.includeShopName,
        custom_message: settings.customMessage
      };

      const response = await fetch(`${backendUrl}/qr-settings/${encodeURIComponent(shopName)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail
        },
        body: JSON.stringify(saveData)
      });

      const result = await response.json();
      if (result.success) {
        alert('QR設定を保存しました');
      } else {
        alert('QR設定の保存に失敗しました: ' + result.error);
      }
    } catch (error) {
      console.error('QR設定保存エラー:', error);
      alert('QR設定保存中にエラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  // アンケートURL生成
  const generateSurveyURL = () => {
    const baseURL = window.location.origin;
    return `${baseURL}/survey/${encodeURIComponent(shopName)}`;
  };

  const surveyURL = generateSurveyURL();

  // QRコードを画像としてダウンロード
  const downloadQR = () => {
    const svg = document.getElementById('qr-code');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = settings.size;
    canvas.height = settings.size;

    img.onload = () => {
      if (ctx) {
        ctx.fillStyle = settings.backgroundColor;
        ctx.fillRect(0, 0, settings.size, settings.size);
        ctx.drawImage(img, 0, 0, settings.size, settings.size);

        // ダウンロード
        const link = document.createElement('a');
        link.download = `${shopName}-アンケートQR.png`;
        link.href = canvas.toDataURL();
        link.click();
      }
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  // URLをクリップボードにコピー
  const copyURL = async () => {
    try {
      await navigator.clipboard.writeText(surveyURL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('コピーに失敗しました');
    }
  };

  // 印刷用CSS（印刷時に最適化）
  const printStyles = `
    @media print {
      body * { visibility: hidden; }
      .print-area, .print-area * { visibility: visible; }
      .print-area { 
        position: absolute; 
        left: 0; 
        top: 0; 
        width: 100%; 
        text-align: center;
        padding: 20px;
      }
      .no-print { display: none !important; }
    }
  `;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <style>{printStyles}</style>
      
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          アンケートQRコード生成
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {showPreview ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {showPreview ? 'プレビュー非表示' : 'プレビュー表示'}
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            <Settings className="h-4 w-4 mr-1" />
            設定
          </button>
        </div>
      </div>

      {/* 設定パネル */}
      {showSettings && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6 no-print">
          <h3 className="text-lg font-medium text-gray-800 mb-4">QRコード設定</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                サイズ: {settings.size}px
              </label>
              <input
                type="range"
                min="150"
                max="400"
                value={settings.size}
                onChange={(e) => setSettings({...settings, size: parseInt(e.target.value)})}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                背景色
              </label>
              <input
                type="color"
                value={settings.backgroundColor}
                onChange={(e) => setSettings({...settings, backgroundColor: e.target.value})}
                className="w-full h-10 rounded border border-gray-300"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                QRコード色
              </label>
              <input
                type="color"
                value={settings.foregroundColor}
                onChange={(e) => setSettings({...settings, foregroundColor: e.target.value})}
                className="w-full h-10 rounded border border-gray-300"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={settings.includeShopName}
                  onChange={(e) => setSettings({...settings, includeShopName: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">店舗名を表示</span>
              </label>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              カスタムメッセージ
            </label>
            <input
              type="text"
              value={settings.customMessage}
              onChange={(e) => setSettings({...settings, customMessage: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="お客様へのメッセージを入力"
            />
          </div>
        </div>
      )}

      {/* QRコードプレビュー */}
      {showPreview && (
        <div className="print-area text-center">
          <div className="inline-block bg-white p-6 rounded-lg border-2 border-gray-200">
            {settings.includeShopName && (
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {shopName}
              </h3>
            )}
            
            {settings.customMessage && (
              <p className="text-sm text-gray-600 mb-4">
                {settings.customMessage}
              </p>
            )}

            <div 
              id="qr-code"
              className="inline-block p-4 rounded-lg"
              style={{ backgroundColor: settings.backgroundColor }}
            >
              <QRCode
                value={surveyURL}
                size={settings.size}
                bgColor={settings.backgroundColor}
                fgColor={settings.foregroundColor}
                level="M"
              />
            </div>

            <div className="mt-4 text-xs text-gray-500">
              <p>スマートフォンのカメラで読み取ってください</p>
              <p className="break-all mt-1">{surveyURL}</p>
            </div>
          </div>
        </div>
      )}

      {/* 操作ボタン */}
      <div className="flex flex-wrap gap-3 mt-6 no-print">
        <button
          onClick={saveQRSettings}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {saving ? '保存中...' : 'QR設定を保存'}
        </button>

        <button
          onClick={downloadQR}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Download className="h-4 w-4 mr-2" />
          画像ダウンロード
        </button>

        <button
          onClick={copyURL}
          className={`flex items-center px-4 py-2 rounded-md transition-colors ${
            copied 
              ? 'bg-green-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <Copy className="h-4 w-4 mr-2" />
          {copied ? 'コピー完了!' : 'URLコピー'}
        </button>

        <button
          onClick={() => window.print()}
          className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
        >
          印刷
        </button>

        <a
          href={surveyURL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
        >
          テスト確認
        </a>
      </div>

      {/* 使用方法ガイド */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg no-print">
        <h4 className="font-medium text-blue-800 mb-2">📋 QRコードの活用方法</h4>
        <div className="text-sm text-blue-700 space-y-1">
          <p>• テーブルに設置してお客様にアンケート協力を依頼</p>
          <p>• レシートに印刷してアンケートへの誘導</p>
          <p>• 店内ポスターに掲載して口コミ投稿を促進</p>
          <p>• SNSで共有して来店前のお客様にもアプローチ</p>
        </div>
      </div>

      {/* 統計情報 */}
      <div className="mt-4 text-xs text-gray-500 no-print">
        <p>店舗: {shopName}</p>
        <p>管理者: {userEmail}</p>
        <p>生成日時: {new Date().toLocaleString('ja-JP')}</p>
      </div>
    </div>
  );
};

export default QRGenerator;